import React, { useEffect, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { makeStyles } from '@mui/styles'
import {
  Theme,
  Collapse,
  IconButton,
  TableCell,
  TableRow,
  TableBody,
  Table,
  TableHead,
  useTheme,
  useMediaQuery,
} from '@mui/material'
import TournamentTableRow from '../table/TournamentTableRow'
import TournamentTableCell from '../table/TournamentTableCell'
import TournamentTable, { TournamentTableHead, TournamentTableBody } from '../table/TournamentTable'
import { FormattedMessage } from 'react-intl'
import { ArrowDropDown, OpenInNew } from '@mui/icons-material'
import { getRanking } from '@app/store/tourAndRanking/actions'
import { rem } from '@app/theme/materialUITheme'
import RankingLeaderboardTitle from './RankingLeaderboardTitle'
import { formTournamentSiteUrl } from '@app/config'
import { formatOutputNumber } from '@app/utils/numbers'
import classNames from 'classnames'
import { RootState } from '@app/store'

const useStyles = makeStyles((theme: Theme) => ({
  root: {
    boxShadow: '0 0 12px 3px rgba(0,0,0,0.15)',
    backgroundColor: theme.customPalette.background,
  },
  flag: {
    display: 'block',
    margin: '0 auto',
    width: 24,
  },
  bold: {
    fontWeight: 'bold',
    color: theme.palette.primary.main,
  },
  rankingTable: {
    color: theme.customPalette.tableTextColor,
    maxHeight: 'none',
  },
  leaderboardPosTitle: {
    width: 40,
    textAlign: 'center',
    [theme.breakpoints.down('sm')]: {
      width: 20,
    },
  },
  leaderboardNameCell: {
    cursor: 'pointer',
    fontWeight: 600,
    [theme.breakpoints.down('sm')]: {
      textOverflow: 'ellipsis',
      maxWidth: 80,
    },
  },
  leaderboardArrowDownTitle: {
    width: 40,
  },
  leaderboardEventsTitle: {
    textAlign: 'center',
  },
  leaderboardPointsTitle: {
    textAlign: 'center',
  },
  eventsTable: {
    background: theme.customPalette.background,
    border: `2px solid ${theme.palette.primary.dark}`,

    '& > thead > tr > th': {
      color: theme.palette.primary.contrastText,
      background: theme.palette.primary.dark,
      fontWeight: 'bold',
      fontSize: rem(14),
      padding: '5px 10px 6px',
      [theme.breakpoints.down('sm')]: {
        fontSize: rem(10),
      },
    },
    '& > tbody > tr > td': {
      padding: '10px',
      fontSize: rem(13),
      border: 0,
      [theme.breakpoints.down('sm')]: {
        fontSize: rem(10),
      },
    },
    '& > tbody > tr.bold > td': {
      fontWeight: 'bold',
    },
  },
  eventsTableNameTitle: {
    whiteSpace: 'nowrap',
    [theme.breakpoints.up('sm')]: {
      paddingRight: 40,
    },
  },
  nameContainer: {
    [theme.breakpoints.down('sm')]: {
      display: 'flex',
      justifyContent: 'space-between',
      alignItems: 'center',
    },
  },
  namePosContainer: {
    [theme.breakpoints.down('sm')]: {
      whiteSpace: 'nowrap',
      textOverflow: 'ellipsis',
      overflow: 'hidden',
    },
  },
  eventsTableNameCell: {
    width: '75%',
    [theme.breakpoints.up('sm')]: {
      paddingRight: 40,
    },
  },
  eventsContainer: {
    whiteSpace: 'wrap',
    wordBreak: 'break-word',

    '& a': {
      position: 'relative',
      '& svg': {
        marginLeft: 7,
        fontSize: 16,
        position: 'absolute',
        top: -1,
      },
    },
  },
  eventsPosCell: {
    whiteSpace: 'nowrap',
    paddingRight: 20,
    textAlign: 'center',
  },
  eventsPointsCell: {
    whiteSpace: 'nowrap',
    textAlign: 'center',
  },
}))

interface Props {
  id: number
}

interface RankingResultRow extends RankingResult {
  position: string
}

export const RankingLeaderboard: React.FC<Props> = ({ id }) => {
  const dispatch = useDispatch()
  const classes = useStyles()

  const theme = useTheme()
  const mediaQuery = useMediaQuery(theme.breakpoints.down('sm'))

  const { rankings } = useSelector((store: RootState) => store.tourAndRankingReducer)
  const ranking = rankings.find((r) => r.id === id)

  const [scoreExpanded, changeScoreExpanded] = useState<number>(undefined)

  useEffect(() => {
    if (id && !rankings.length) {
      dispatch(getRanking(id))
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  if (!ranking) {
    return
  }

  const _renderRow = (i: number, result: RankingResultRow, bestResultsCount?: number) => {
    const expanded = scoreExpanded === result.playerId

    return (
      <React.Fragment key={result.playerId}>
        <TournamentTableRow withBg={i % 2 === 0 ? true : false} expanded={expanded} rowKey={String(result.playerId)}>
          <TournamentTableCell style={{ width: 40, textAlign: 'center', fontWeight: 600 }}>
            {result.position}
          </TournamentTableCell>
          {!mediaQuery && (
            <TournamentTableCell style={{ width: 40, textAlign: 'center' }}>
              {result.countryCode && (
                <img
                  src={`//static.golfgamebook.com/flags/${result.countryCode}.png`}
                  alt={result.countryCode}
                  className={classes.flag}
                />
              )}
            </TournamentTableCell>
          )}
          <TournamentTableCell className={classNames([classes.leaderboardNameCell])}>
            <div className={classes.nameContainer}>
              <div className={classes.namePosContainer}>
                <span
                  onClick={() => {
                    const newId: number = expanded ? 0 : result.playerId
                    changeScoreExpanded(newId)
                  }}
                >
                  {result.playerName}
                </span>
              </div>
            </div>
          </TournamentTableCell>
          <TournamentTableCell>
            <IconButton
              style={{
                padding: 0,
                margin: 0,
                color: expanded ? '#fff' : 'inherit',
                transform: expanded ? 'rotate(180deg)' : 'none',
              }}
              onClick={() => {
                const newId: number = expanded ? 0 : result.playerId
                changeScoreExpanded(newId)
              }}
              size="large"
            >
              <ArrowDropDown />
            </IconButton>
          </TournamentTableCell>
          <TournamentTableCell style={{ textAlign: 'center', fontWeight: 500 }}>
            <strong>{result.events.length}</strong>
          </TournamentTableCell>
          <TournamentTableCell style={{ textAlign: 'center' }}>
            <span className={expanded ? '' : classes.bold}>
              <strong>{formatOutputNumber(result.points)}</strong>
            </span>
          </TournamentTableCell>
        </TournamentTableRow>
        <TournamentTableRow>
          <TournamentTableCell colSpan={6} style={{ padding: 0 }}>
            <Collapse in={expanded}>{_renderEventsTable(result.playerId, result.events, bestResultsCount)}</Collapse>
          </TournamentTableCell>
        </TournamentTableRow>
      </React.Fragment>
    )
  }

  const _calculatePositions = (results: RankingResult[]): RankingResultRow[] => {
    results.sort((a, b) => b.points - a.points)
    let lastPoints = null
    let lastPosition = null

    return results.map((row, idx) => {
      const position = row.points === lastPoints ? lastPosition : idx + 1
      lastPoints = row.points
      lastPosition = position
      return { ...row, position: `${position}.` } as RankingResultRow
    }) as RankingResultRow[]
  }

  const _renderEventsTable = (playerId: number, events: RankingResultEvent[], bestResultsCount?: number) => {
    return (
      <Table className={classes.eventsTable} key={`events-${playerId}`}>
        <TableHead>
          <TableRow>
            <TableCell className={classes.eventsTableNameTitle}>
              <FormattedMessage id="leaderboard.events" />
              {bestResultsCount && (
                <span style={{ fontWeight: 400, marginLeft: 10, fontSize: '90%' }}>
                  (<FormattedMessage id="leaderboard.bestResultsCount" values={{ count: bestResultsCount }} />)
                </span>
              )}
            </TableCell>
            <TableCell style={{ whiteSpace: 'nowrap', textAlign: 'center' }}>
              <FormattedMessage id="leaderboard.position" />
            </TableCell>
            <TableCell style={{ whiteSpace: 'nowrap', textAlign: 'center' }}>
              <FormattedMessage id="leaderboard.points" />
            </TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {events.map((event, i) => (
            <TableRow
              key={i}
              sx={i % 2 === 0 ? { bgcolor: '#F5F5F5' } : {}}
              className={bestResultsCount && i < bestResultsCount ? 'bold' : ''}
            >
              <TableCell className={classes.eventsTableNameCell}>
                <div className={classes.eventsContainer}>
                  {event.name}
                  {event.tournamentSite && (
                    <a href={formTournamentSiteUrl(event.tournamentSite)}>
                      <OpenInNew color="primary" />
                    </a>
                  )}
                </div>
              </TableCell>
              <TableCell className={classes.eventsPosCell}>{event.position ? `${event.position}.` : ''}</TableCell>
              <TableCell className={classes.eventsPointsCell}>{event.points}</TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    )
  }

  const leaderboardRows = _calculatePositions(ranking.leaderboard)

  return (
    <div className={classes.root}>
      <RankingLeaderboardTitle ranking={ranking} />
      <TournamentTable className={classes.rankingTable}>
        <TournamentTableHead>
          <TournamentTableRow darkBg>
            <TournamentTableCell element="th" className={classes.leaderboardPosTitle}>
              {!mediaQuery && <FormattedMessage id="leaderboard.pos" />}
            </TournamentTableCell>
            {!mediaQuery && (
              <TournamentTableCell element="th">
                <FormattedMessage id="leaderboard.country" />
              </TournamentTableCell>
            )}
            <TournamentTableCell element="th">
              <FormattedMessage id="leaderboard.name" />
            </TournamentTableCell>
            <TournamentTableCell className={classes.leaderboardArrowDownTitle}></TournamentTableCell>
            <TournamentTableCell element="th" className={classNames([classes.leaderboardEventsTitle])}>
              <FormattedMessage id="leaderboard.events" />
            </TournamentTableCell>
            <TournamentTableCell element="th" className={classNames([classes.leaderboardPointsTitle])}>
              <FormattedMessage id="leaderboard.points" />
            </TournamentTableCell>
          </TournamentTableRow>
        </TournamentTableHead>
        <TournamentTableBody>
          {leaderboardRows.map((entry, i) => _renderRow(i, entry, ranking.bestResultsCount))}
        </TournamentTableBody>
      </TournamentTable>
    </div>
  )
}
